/**
 * Phase 8 banners / announcements (PRODUCT_REQUIREMENTS §13). Informational only: INFO / WARNING tone,
 * scheduled window, live preview, enable/disable with reason + step-up + audit (server-enforced).
 * Banners cannot imitate or hide financial/security warnings: they render in their own strip on the
 * user Home, never inside money flows, and carry no action beyond an internal link.
 */
import { AppContainer, Badge, Button, Card, cx, DataTable, PageHeader, TextField } from '@hopex/ui';
import { Info, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { api, dt } from '../api.ts';
import { CommandDialog, Facts } from '../lib/command.tsx';
import { Remote, useRemote } from '../lib/remote.tsx';

interface BannerRow {
  id: string;
  title: string;
  body: string;
  tone: 'INFO' | 'WARNING';
  linkPath: string | null;
  priority: number;
  startsAt: string;
  endsAt: string;
  enabled: boolean;
  live: boolean;
  version: string;
}

const TONE = { INFO: 'اطلاع‌رسانی', WARNING: 'هشدار' } as const;

function Preview({ title, body, tone }: { title: string; body: string; tone: 'INFO' | 'WARNING' }) {
  const Icon = tone === 'WARNING' ? TriangleAlert : Info;
  return (
    <div
      className={cx(
        'flex items-start gap-3 rounded-md border px-4 py-3',
        tone === 'WARNING' ? 'border-pending/40 bg-surface-2' : 'border-accent-soft bg-accent-tint',
      )}
      data-banner-preview
    >
      <Icon
        aria-hidden
        className={cx(
          'mt-1 size-4 shrink-0',
          tone === 'WARNING' ? 'text-pending' : 'text-accent-text',
        )}
      />
      <span className="min-w-0">
        <strong className="block text-[13.5px]">{title || 'عنوان اطلاعیه'}</strong>
        <span className="block text-[13px] leading-6 text-fg-muted">{body || 'متن اطلاعیه'}</span>
      </span>
    </div>
  );
}

/** Local datetime-input value (browser time zone) ↔ ISO. */
const toLocalInput = (d: Date) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

export function AnnouncementsPage() {
  const r = useRemote<{ items: BannerRow[] }>('/banners');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tone, setTone] = useState<'INFO' | 'WARNING'>('INFO');
  const [link, setLink] = useState('');
  const [starts, setStarts] = useState(() => toLocalInput(new Date()));
  const [ends, setEnds] = useState(() => toLocalInput(new Date(Date.now() + 3 * 86_400_000)));
  const [confirm, setConfirm] = useState(false);
  const [toggle, setToggle] = useState<BannerRow | null>(null);
  const valid =
    title.trim().length >= 3 &&
    body.trim().length >= 3 &&
    (!link || /^\/[a-z0-9/_-]{0,80}$/.test(link)) &&
    new Date(ends) > new Date(starts);

  return (
    <AppContainer>
      <PageHeader
        title="اعلان‌ها و بنرها"
        description="اطلاعیه‌های داشبورد کاربران. فقط اطلاع‌رسانی؛ جایگزین هشدار مالی یا امنیتی نمی‌شود."
      />
      <div className="mb-6 grid gap-4 xl:grid-cols-2">
        <Card className="flex flex-col gap-3 p-4 sm:p-5" data-banner-form>
          <h2 className="m-0 text-[14px] font-bold">اطلاعیه‌ی جدید</h2>
          <TextField label="عنوان (۳ تا ۸۰ نویسه)" value={title} onChange={setTitle} />
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
            متن (۳ تا ۲۸۰ نویسه)
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={280}
              className="rounded-md border border-line-strong bg-surface-1 p-3 text-[13.5px] font-normal leading-7"
              data-banner-body
            />
          </label>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
              لحن
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value as 'INFO' | 'WARNING')}
                className="h-10 rounded-md border border-line-strong bg-surface-1 px-3 text-[13px] font-normal text-fg"
              >
                <option value="INFO">{TONE.INFO}</option>
                <option value="WARNING">{TONE.WARNING}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
              شروع
              <input
                type="datetime-local"
                value={starts}
                onChange={(e) => setStarts(e.target.value)}
                className="h-10 rounded-md border border-line-strong bg-surface-1 px-2 text-[13px] text-fg"
                dir="ltr"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
              پایان
              <input
                type="datetime-local"
                value={ends}
                onChange={(e) => setEnds(e.target.value)}
                className="h-10 rounded-md border border-line-strong bg-surface-1 px-2 text-[13px] text-fg"
                dir="ltr"
              />
            </label>
          </div>
          <TextField
            label="پیوند داخلی (اختیاری، مثلاً /support)"
            value={link}
            onChange={setLink}
            ltr
          />
          <Button
            className="w-fit"
            isDisabled={!valid}
            onPress={() => setConfirm(true)}
            data-banner-create
          >
            بررسی و انتشار
          </Button>
        </Card>
        <Card className="flex flex-col gap-3 p-4 sm:p-5">
          <h2 className="m-0 text-[14px] font-bold">پیش‌نمایش در داشبورد کاربر</h2>
          <Preview title={title} body={body} tone={tone} />
          <p className="m-0 text-[12px] text-fg-subtle">
            بنرها فقط در نوار اطلاعیه‌ی داشبورد نمایش داده می‌شوند، نه در جریان واریز، برداشت یا
            تبدیل. ادعای تبلیغاتی یا مالی بدون تأیید مالک منتشر نکنید.
          </p>
        </Card>
      </div>
      <Remote r={r}>
        {(d) => (
          <Card className="p-3 sm:p-4" data-banner-history>
            <DataTable<BannerRow>
              label="تاریخچه‌ی اطلاعیه‌ها"
              rows={d.items}
              columns={[
                { id: 'title', header: 'عنوان', isRowHeader: true, cell: (b) => b.title },
                { id: 'tone', header: 'لحن', cell: (b) => TONE[b.tone] },
                {
                  id: 'state',
                  header: 'وضعیت',
                  cell: (b) => (
                    <Badge tone={b.live ? 'positive' : b.enabled ? 'accent' : 'neutral'}>
                      {b.live
                        ? 'در حال نمایش'
                        : b.enabled
                          ? 'زمان‌بندی‌شده / پایان‌یافته'
                          : 'غیرفعال'}
                    </Badge>
                  ),
                },
                {
                  id: 'window',
                  header: 'بازه',
                  cell: (b) => `${dt(b.startsAt)} ← ${dt(b.endsAt)}`,
                },
                {
                  id: 'actions',
                  header: 'عملیات',
                  cell: (b) => (
                    <Button
                      size="sm"
                      variant={b.enabled ? 'danger' : 'secondary'}
                      onPress={() => setToggle(b)}
                      data-banner-toggle={b.enabled ? 'off' : 'on'}
                    >
                      {b.enabled ? 'غیرفعال‌سازی' : 'فعال‌سازی'}
                    </Button>
                  ),
                },
              ]}
              empty={
                <p className="m-0 text-center text-[13px] text-fg-muted">
                  هنوز اطلاعیه‌ای ثبت نشده است.
                </p>
              }
            />
          </Card>
        )}
      </Remote>
      {confirm && (
        <CommandDialog
          title="انتشار اطلاعیه"
          confirmLabel="انتشار"
          onClose={() => setConfirm(false)}
          onDone={() => {
            setConfirm(false);
            setTitle('');
            setBody('');
            setLink('');
            r.reload();
          }}
          run={async (reason) => {
            await api.post('/banners', {
              title,
              body,
              tone,
              linkPath: link || null,
              startsAt: new Date(starts).toISOString(),
              endsAt: new Date(ends).toISOString(),
              reason,
            });
          }}
        >
          <Preview title={title} body={body} tone={tone} />
          <Facts
            rows={[
              [
                'بازه',
                `${dt(new Date(starts).toISOString())} ← ${dt(new Date(ends).toISOString())}`,
              ],
              ['لحن', TONE[tone]],
            ]}
          />
        </CommandDialog>
      )}
      {toggle && (
        <CommandDialog
          title={toggle.enabled ? 'غیرفعال‌سازی اطلاعیه' : 'فعال‌سازی اطلاعیه'}
          tone={toggle.enabled ? 'danger' : 'primary'}
          onClose={() => setToggle(null)}
          onDone={() => {
            setToggle(null);
            r.reload();
          }}
          run={async (reason) => {
            await api.post(`/banners/${toggle.id}`, {
              enabled: !toggle.enabled,
              expectedVersion: toggle.version,
              reason,
            });
          }}
        >
          <Facts rows={[['عنوان', toggle.title]]} />
        </CommandDialog>
      )}
    </AppContainer>
  );
}
